import React, { useMemo } from "react";
import { useVM } from "@src/hooks/useVM";
import { makeAutoObservable, reaction } from "mobx";
import { RootStore, useStores } from "@stores";
import BN from "@src/utils/BN";
import aggregatorService, { TCalcRoute } from "@src/services/aggregatorService";
import { CONTRACT_ADDRESSES, EXPLORER_URL, IToken, ROUTES, TOKENS_BY_ASSET_ID, TOKENS_BY_SYMBOL } from "@src/constants";
import nodeService from "@src/services/nodeService";
import { isDryRunEnabled, getCustomPublicKey } from "@src/utils/userSettings";
import { TGatedFeature } from "@src/constants/featureAccess";

interface IProps {
  children: React.ReactNode;
}

export interface ITradeAction {
  name: string;
  route: string;
  /** Set when the tab is only shown to whitelisted accounts. */
  feature?: TGatedFeature;
}

// The index of an action in this list is what `activeAction` holds.
export const TRADE_ACTIONS: ITradeAction[] = [
  { name: "Swap", route: ROUTES.TRADE },
  { name: "Limit", route: ROUTES.LIMIT_ORDER },
  { name: "DCA", route: ROUTES.DCA, feature: "dca" }
];

export const ACTION_ROUTES = TRADE_ACTIONS.map(({ route }) => route);

export const pathnameToActiveAction = (pathname: string): number => {
  const index = ACTION_ROUTES.indexOf(pathname);
  return index === -1 ? 0 : index;
};

const ctx = React.createContext<SwapVM | null>(null);

export const SwapVMProvider: React.FC<IProps> = ({ children }) => {
  const rootStore = useStores();
  const store = useMemo(() => new SwapVM(rootStore), [rootStore]);
  return <ctx.Provider value={store}>{children}</ctx.Provider>;
};

export const useSwapVM = () => useVM(ctx);

export class SwapVM {
  constructor(private rootStore: RootStore) {
    makeAutoObservable(this);
    this.setActiveAction(pathnameToActiveAction(window.location.pathname));
    const params = new URLSearchParams(window.location.search);
    const asset0 = params.get("asset0")?.toString();
    const asset1 = params.get("asset1")?.toString();
    this.assetId0 = asset0 ?? TOKENS_BY_SYMBOL.WAVES.assetId;
    this.assetId1 = asset1 ?? TOKENS_BY_SYMBOL.PUZZLE.assetId;
    this._syncAmount1();

    reaction(
      () => [this.assetId0, this.assetId1],
      () => {
        if (this.inputMode === "send") {
          this._syncAmount1();
        } else {
          this._syncAmount0();
        }
      }
    );

    reaction(
      () => this.amount0,
      (_, prev) => {
        // Only sync if inputMode is "send" AND this is a user-initiated change (not from _syncAmount0)
        if (this.inputMode === "send" && !this.synchronizing) {
          this._syncAmount1();
        }
      }
    );

    setInterval(() => {
      if (this.inputMode === "send") {
        this._syncAmount1(true);
      } else {
        this._syncAmount0(true);
      }
    }, 15 * 1000);
  }

  public activeAction: number = 0;
  setActiveAction = (v: number) => (this.activeAction = v);

  openedChart = localStorage.getItem("swap-chart-opened") === "true";
  setOpenedChart = (v: boolean) => {
    this.openedChart = v;
    localStorage.setItem("swap-chart-opened", String(v));
  };

  openedHistory = localStorage.getItem("swap-history-opened") === "true";
  setOpenedHistory = (v: boolean) => {
    this.openedHistory = v;
    localStorage.setItem("swap-history-opened", String(v));
  };

  chartType: "standard" | "tradingview" = (localStorage.getItem("swap-chart-type") as "standard" | "tradingview") || "standard";
  setChartType = (v: "standard" | "tradingview") => {
    this.chartType = v;
    localStorage.setItem("swap-chart-type", v);
  };
  openedSettings = false;
  setOpenedSettings = (v: boolean) => (this.openedSettings = v);

  // Track last request to prevent duplicates
  private lastRequestKey: string | null = null;

  inputMode: "send" | "receive" = "send";
  setInputMode = (mode: "send" | "receive") => (this.inputMode = mode);

  slippage = new BN(50);
  setSlippage = (v: BN) => (this.slippage = v);

  price: BN = BN.ZERO;
  private _setPrice = (price: BN) => (this.price = price);

  private _calculatePrice(amount0: BN = this.amount0, amount1: BN = this.amount1) {
    const price = BN.formatUnits(amount1, this.token1.decimals)
      .div(BN.formatUnits(amount0, this.token0.decimals))
      .times(0.9971);
    this._setPrice(!price.isNaN() ? price : BN.ZERO);
  }

  parameters: string | null = null;
  private _setParameters = (parameters: string | null) => (this.parameters = parameters);

  aggregatorResponse: any = null;
  private _setAggregatorResponse = (response: any) => (this.aggregatorResponse = response);

  lastErrorAggregatorResponse: any = null;
  private _setLastErrorAggregatorResponse = (response: any) => (this.lastErrorAggregatorResponse = response);

  synchronizing: boolean = false;
  private _setSynchronizing = (synchronizing: boolean) => (this.synchronizing = synchronizing);

  loading: boolean = false;
  private _setLoading = (l: boolean) => (this.loading = l);

  priceImpact: BN = BN.ZERO;
  private _setPriceImpact = (priceImpact: BN) => (this.priceImpact = priceImpact);

  route: Array<TCalcRoute> = [];
  private _setRoute = (route: Array<TCalcRoute>) => (this.route = route);

  aggregatedProfit: BN = BN.ZERO;
  private _setAggregatedProfit = (value: BN) => (this.aggregatedProfit = value);

  assetId0: string;
  setAssetId0 = (assetId: string) => (this.assetId0 = assetId);

  assetId1: string;
  setAssetId1 = (assetId: string) => (this.assetId1 = assetId);

  amount0: BN = BN.ZERO;
  setAmount0 = (amount: BN) => (this.amount0 = amount);

  get amount0MaxClickFunc(): (() => void) | undefined {
    const { token0, balance0 } = this;
    return token0 != null && balance0 != null ? () => this.setAmount0(balance0) : undefined;
  }

  get usdnEquivalent0() {
    const rate = this.rootStore.poolsStore.usdtRate(this.token0.assetId);
    const value = BN.formatUnits(this.amount0, this.token0.decimals).times(rate ?? BN.ZERO);
    const format = value.eq(0) ? 0 : value?.gt(0.0001) ? 2 : 4;
    return "$ " + value.toFormat(format);
  }

  get usdnEquivalent1() {
    const rate = this.rootStore.poolsStore.usdtRate(this.token1.assetId);
    const value = BN.formatUnits(this.amount1, this.token1.decimals).times(rate ?? BN.ZERO);
    const format = value.eq(0) ? 0 : value?.gt(0.0001) ? 2 : 4;
    return "$ " + value.toFormat(format);
  }

  amount1: BN = BN.ZERO;
  private _setAmount1 = (amount: BN) => (this.amount1 = amount);

  setAmount1 = (amount: BN) => {
    this.amount1 = amount;
    if (this.inputMode === "receive") {
      this._syncAmount0();
    }
  };

  routingModalOpened: boolean = false;
  setRoutingModalState = (state: boolean) => (this.routingModalOpened = state);

  swapCounter: number = 0;
  private incrementSwapCounter = () => this.swapCounter++;

  rejectAggregatorPromise?: () => void;
  setRejectAggregatorPromise = (v: any) => (this.rejectAggregatorPromise = v);

  //todo cun out kludge with invalidAmount
  private _syncAmount1 = async (quiet = false) => {
    const { accountStore } = this.rootStore;
    const { amount0, assetId0, assetId1 } = this;
    const invalidAmount = amount0 == null || amount0.isNaN() || amount0.lte(0);
    if (amount0 != null && amount0.eq(0)) {
      this._setAmount1(BN.ZERO);
    }
    const defaultAmount0 = BN.parseUnits(1, this.token0.decimals);
    const amountToUse = invalidAmount ? defaultAmount0 : amount0;

    // Prevent duplicate requests with same parameters
    const requestKey = `${assetId0}-${assetId1}-${amountToUse.toString()}`;
    if (this.synchronizing && this.lastRequestKey === requestKey) {
      return;
    }
    this.lastRequestKey = requestKey;

    !quiet && this._setSynchronizing(true);
    if (this.rejectAggregatorPromise != null) this.rejectAggregatorPromise();
    const promise = new Promise((resolve, reject) => {
      this.rejectAggregatorPromise = reject;
      resolve(aggregatorService.calc(assetId0, assetId1, amountToUse));
    });
    promise
      .then(async (v: any) => {
        this._setAggregatorResponse(v);
        this._calculatePrice(amountToUse, new BN(v.estimatedOut));
        !invalidAmount &&
          this._setPriceImpact((new BN(v.priceImpact).gt(0) ? new BN(v.priceImpact) : BN.ZERO).times(100));
        this._setParameters(!invalidAmount ? v.parameters : null);
        this._setRoute(v.routes);
        this._setAggregatedProfit(new BN(v.aggregatedProfit));

        // If dry run enabled and user connected, get exact amount from contract
        if (!invalidAmount && isDryRunEnabled() && v.parameters) {
          const publicKey = await accountStore.getPublicKey();
          if (publicKey) {
            try {
              const minToReceive = new BN(v.estimatedOut * 0.9971).times(new BN(100 - 1).div(100));
              const dryRunResult = await nodeService.dryRunSwap(
                publicKey,
                v.parameters,
                minToReceive.toFixed(0),
                this.token1.assetId === "WAVES" ? "WAVES" : this.token1.assetId,
                {
                  assetId: this.token0.assetId === "WAVES" ? null : this.token0.assetId,
                  amount: amountToUse.toString()
                }
              );
              if (dryRunResult.success && dryRunResult.estimatedOut) {
                this._setAmount1(new BN(dryRunResult.estimatedOut));
                this._setSynchronizing(false);
                return;
              }
            } catch (e) {
              // Fall back to aggregator estimate
            }
          }
        }

        !invalidAmount && this._setAmount1(new BN(v.estimatedOut * 0.9971));
        this._setSynchronizing(false);
      })
      .catch((error) => {
        this._setAggregatorResponse({ error: error.message || 'Unknown error' });
        this._setAmount1(BN.ZERO);
        this._setPriceImpact(BN.ZERO);
        this._setRoute([]);
        this._setPrice(BN.ZERO);
        this._setParameters(null);
      })
      .finally(() => {
        this.setRejectAggregatorPromise(undefined);
        this._setSynchronizing(false);
      });
  };

  private _syncAmount0 = (quiet = false) => {
    const { amount1, assetId0, assetId1 } = this;
    const invalidAmount = amount1 == null || amount1.isNaN() || amount1.lte(0);

    if (amount1 != null && amount1.eq(0)) {
      this.setAmount0(BN.ZERO);
      return;
    }

    const rate0 = this.rootStore.poolsStore.usdtRate(assetId0);
    const rate1 = this.rootStore.poolsStore.usdtRate(assetId1);

    if (!rate0 || !rate1 || rate0.eq(0) || rate1.eq(0)) {
      return;
    }

    const amount1Formatted = BN.formatUnits(amount1, this.token1.decimals);
    const usdValue = amount1Formatted.times(rate1);
    const estimatedAmount0Formatted = usdValue.div(rate0).times(1.01);
    const estimatedAmount0 = new BN(BN.parseUnits(estimatedAmount0Formatted, this.token0.decimals).toFixed(0));

    const defaultAmount0 = BN.parseUnits(1, this.token0.decimals);
    const amountToUse = invalidAmount ? defaultAmount0 : estimatedAmount0;

    // Prevent duplicate requests with same parameters
    const requestKey = `${assetId0}-${assetId1}-${amountToUse.toString()}`;
    if (this.synchronizing && this.lastRequestKey === requestKey) {
      return;
    }
    this.lastRequestKey = requestKey;

    !quiet && this._setSynchronizing(true);
    if (this.rejectAggregatorPromise != null) this.rejectAggregatorPromise();

    const promise = new Promise((resolve, reject) => {
      this.rejectAggregatorPromise = reject;
      resolve(aggregatorService.calc(assetId0, assetId1, amountToUse));
    });

    promise
      .then((v: any) => {
        this._setAggregatorResponse(v);

        if (!invalidAmount) {
          this.amount0 = estimatedAmount0;
          // Пересчитываем точное значение получения
          this._setAmount1(new BN(v.estimatedOut * 0.9971));
        }

        this._calculatePrice(estimatedAmount0, new BN(v.estimatedOut));
        this._setSynchronizing(false);
        !invalidAmount &&
          this._setPriceImpact((new BN(v.priceImpact).gt(0) ? new BN(v.priceImpact) : BN.ZERO).times(100));
        this._setParameters(!invalidAmount ? v.parameters : null);
        this._setRoute(v.routes);
        this._setAggregatedProfit(new BN(v.aggregatedProfit));
      })
      .catch((error) => {
        this._setAggregatorResponse({ error: error.message || 'Unknown error' });
        this.amount0 = BN.ZERO;
        this._setPriceImpact(BN.ZERO);
        this._setRoute([]);
        this._setPrice(BN.ZERO);
        this._setParameters(null);
      })
      .finally(() => {
        this.setRejectAggregatorPromise(undefined);
        this._setSynchronizing(false);
      });
  };

  get token0() {
    return this.rootStore.accountStore.balances.find(({ assetId }) => assetId === this.assetId0)!;
  }

  get balance0() {
    return this.token0?.balance;
  }

  get token1() {
    return this.rootStore.accountStore.balances.find(({ assetId }) => assetId === this.assetId1)!;
  }

  get simpleRoute() {
    if (this.route == null || this.route.length <= 0 || this.route[0].exchanges.length <= 0) {
      return null;
    }

    const simpleRoute = this.route[0].exchanges.reduce<Array<string>>(
      (acc, e, i) => [
        ...acc,
        ...(i === 0 ? [e.from, e.to] : [e.to]).map((v) => {
          const asset = this.getBalanceByAssetId(v);
          return asset != null ? asset.symbol : "UNKNOWN";
        })
      ],
      []
    );

    return simpleRoute.length < 4 ? simpleRoute : [simpleRoute[0], "...", simpleRoute[simpleRoute.length - 1]];
  }

  getBalanceByAssetId = (assetId: string) => this.rootStore.accountStore.balances.find((b) => assetId === b.assetId);

  get minimumToReceive(): BN {
    const slippage = JSON.parse(localStorage.getItem("puzzle-user-settings") || '{"slippage": 1}')?.slippage || 1;
    return this.amount1.times(new BN(100 - slippage).div(100));
  }

  switchTokens = () => {
    const assetId0 = this.assetId0;
    const prevAmount1 = new BN(this.amount1.toFixed(0));
    this.setAssetId0(this.assetId1);
    this.setAssetId1(assetId0);
    this.amount0 = prevAmount1;
    this.amount1 = BN.ZERO;
    this.inputMode = "send";
    this._syncAmount1();
  };

  swap = async () => {
    const { accountStore, notificationStore } = this.rootStore;
    const { token0, token1, amount0, minimumToReceive, parameters } = this;
    if (this.synchronizing || parameters == null) return;
    if (token0 == null || amount0.eq(0)) return;
    if (minimumToReceive == null) return;
    await this._syncAmount1();
    this._setLoading(true);
    const currentAggregatorResponse = this.aggregatorResponse;

    // Dry-run check if enabled
    if (isDryRunEnabled()) {
      const publicKey = await accountStore.getPublicKey();
      if (publicKey) {
        try {
          const dryRunResult = await nodeService.dryRunSwap(
            publicKey,
            parameters,
            minimumToReceive.toFixed(0),
            token1.assetId === "WAVES" ? "WAVES" : token1.assetId,
            {
              assetId: token0.assetId === "WAVES" ? null : token0.assetId,
              amount: amount0.toString()
            }
          );

          if (!dryRunResult.success) {
            this._setLoading(false);
            notificationStore.notify(dryRunResult.error || "Dry run failed", {
              type: "warning",
              title: "Swap simulation failed"
            });
            return;
          }

          // Show estimated result from dry-run
          const estimatedOut = dryRunResult.estimatedOut;
          if (estimatedOut) {
            const formattedOut = BN.formatUnits(new BN(estimatedOut), token1.decimals);
            notificationStore.notify(
              `Simulation OK: ~${formattedOut.toFormat(6)} ${token1.symbol}`,
              { type: "info", title: "Dry run passed", duration: 3 }
            );
          }
        } catch (e: any) {
          this._setLoading(false);
          notificationStore.notify(e.message || "Dry run failed", {
            type: "warning",
            title: "Swap simulation failed"
          });
          return;
        }
      }
    }

    await accountStore
      .invoke({
        dApp: CONTRACT_ADDRESSES.aggregator,
        payment: [
          {
            assetId: token0.assetId === "WAVES" ? null : token0.assetId,
            amount: amount0.toString()
          }
        ],
        call: {
          function: "swap",
          args: [
            { type: "string", value: parameters },
            {
              type: "integer",
              value: minimumToReceive.toFixed(0).toString()
            }
          ]
        }
      })
      .then(async (txId) => {
        if (txId) {
          try {
            const txResponse = await fetch(`https://nodes.wx.network/transactions/info/${txId}`);
            const tx = await txResponse.json();

            const paymentAssetId = tx.payment[0].assetId ?? "WAVES";
            const decimals_in = token0.decimals;
            const decimals_out = token1.decimals;

            const out = (
              tx.payment[0].amount -
              (
                tx.stateChanges.transfers.find(
                  (t: any) => t.address === tx.sender && t.asset === paymentAssetId
                )?.amount || 0
              )
            ) / (10 ** decimals_in);

            const inn = (
              tx.stateChanges.transfers.find(
                (t: any) => t.address === tx.sender && t.asset !== paymentAssetId
              )?.amount || 0
            ) / (10 ** decimals_out);

            notificationStore.notify(
              `You swapped ${out.toFixed(6)} ${token0.symbol} ➔ ${inn.toFixed(6)} ${token1.symbol}`,
              {
                type: "success",
                title: "Swap completed successfully!",
                link: `${EXPLORER_URL}/transactions/${txId}`,
                linkTitle: "View on Explorer"
              }
            );
          } catch (error) {
            notificationStore.notify("You can view the details of it in explorer", {
              type: "success",
              title: "Transaction is completed",
              link: `${EXPLORER_URL}/transactions/${txId}`,
              linkTitle: "View on Explorer"
            });
          }
        }
      })
      .catch((e) => {
        this._setLastErrorAggregatorResponse(currentAggregatorResponse);
        notificationStore.notify(e.message ?? JSON.stringify(e), {
          type: "warning",
          title: "Transaction is not completed",
          copyData: {
            route: currentAggregatorResponse?.parameters || null,
            aggregatorError: currentAggregatorResponse?.error || null,
            transactionError: e.message ?? JSON.stringify(e)
          },
          copyText: "Copy error"
        });
      })
      .then(() => this._setLoading(false))
      .then(() => this.incrementSwapCounter())
      .then(() => accountStore.updateAccountAssets(true));
  };

  get totalLiquidity() {
    if (this.rootStore.poolsStore == null) return "";
    const liq = this.rootStore.poolsStore.pools.reduce((acc, pool) => acc.plus(pool.globalLiquidity), BN.ZERO);
    return liq.toFormat(2);
  }

  get schemaValues() {
    if (this.route == null || this.route.length <= 0 || this.route[0].exchanges.length <= 0) {
      return null;
    }
    return this.route.reduce<Array<ISchemaRoute>>((acc, v) => {
      const exchanges = v.exchanges.reduce<Array<ISchemaExchange>>((ac, v) => {
        const token0 = TOKENS_BY_ASSET_ID[v.from];
        const token1 = TOKENS_BY_ASSET_ID[v.to];
        const top = BN.formatUnits(v.amountOut, token1?.decimals);
        const bottom = BN.formatUnits(v.amountIn, token0?.decimals);
        const rate = top.div(bottom);

        const type = v.type;
        return [...ac, { rate, token0, token1, type }];
      }, []);
      const percent = this.amount0.eq(0) ? new BN(100) : new BN(v.in).times(new BN(100)).div(this.amount0);
      return [...acc, { percent: percent, exchanges }];
    }, []);
  }
}

export interface ISchemaRoute {
  percent: BN;
  exchanges: ISchemaExchange[];
}

export interface ISchemaExchange {
  rate: BN;
  token0?: IToken;
  token1?: IToken;
  type: string;
}
